/**
 * FilterChip / FilterSectionTitle — the canonical filter-panel primitives.
 *
 * Every Crop Circle filter panel (activity / asset / account `FilterContent`,
 * the Campaigns panel, the Pulse/OEM home dashboard panel) renders the same
 * thing: an uppercase section title over a wrapping row of selectable pills.
 * These two components own that look so the panels can't drift — pull them in
 * rather than re-deriving the chip styles locally.
 *
 * `filterChipStyleValues` exposes the raw style objects for panels that still
 * render chips inline (FilterContent's severity / column / date controllers);
 * they build their local StyleSheet from these so there is one source of truth.
 */
import { ReactNode } from 'react';
import {
  Pressable,
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  ViewStyle,
} from 'react-native';
import { tokens } from '../crop-circle/tokens';
import { Colors } from '../../themes/colors';

/**
 * Canonical filter-chip visual contract, as plain style objects so inline-chip
 * panels can compose them into their own StyleSheet (see FilterContent).
 */
export const filterChipStyleValues: {
  section: ViewStyle;
  sectionTitle: TextStyle;
  optionsContainer: ViewStyle;
  chip: ViewStyle;
  chipSelected: ViewStyle;
  chipText: TextStyle;
  chipTextSelected: TextStyle;
  chipDisabled: ViewStyle;
} = {
  section: {
    paddingTop: tokens.spacing['2xl'],
  },
  sectionTitle: {
    ...tokens.typography.label.largeEmphasised,
    color: Colors.role.textPrimary,
    marginBottom: tokens.spacing.md,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  optionsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: tokens.spacing.sm,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: 10,
    borderRadius: tokens.borderRadius['3xl'],
    backgroundColor: Colors.role.surfaceHover,
    gap: 6,
  },
  chipSelected: {
    backgroundColor: Colors.role.surfaceInverse,
    borderColor: Colors.role.surfaceInverse,
  },
  chipText: {
    ...tokens.typography.label.large,
    fontWeight: tokens.typography.label.largeEmphasised.fontWeight,
    color: Colors.role.textStrong,
  },
  chipTextSelected: {
    color: Colors.role.textOnInverse,
  },
  chipDisabled: {
    opacity: 0.6,
  },
};

const styles = StyleSheet.create({
  ...filterChipStyleValues,
  /**
   * Component-only (not part of the exported contract): lets an over-long
   * label — e.g. a user's name on the chat-options member chips — truncate
   * with an ellipsis instead of pushing the chip wider than its row.
   */
  chipLabel: {
    flexShrink: 1,
  },
});

export interface FilterChipProps {
  label: string;
  selected: boolean;
  onPress: () => void;
  /**
   * Optional element rendered before the label — a colour dot (home dashboard)
   * or a type icon. The chip lays it out with the canonical 6px gap.
   */
  leading?: ReactNode;
  /**
   * Optional element rendered after the label — e.g. the "+" affordance on the
   * chat-options "add member" chips. Same canonical 6px gap as `leading`.
   */
  trailing?: ReactNode;
  disabled?: boolean;
  testID?: string;
  /** Defaults to `label`. */
  accessibilityLabel?: string;
}

/**
 * A single selectable filter pill. Selected = dark fill / white text; otherwise
 * neutral fill / dark text.
 */
export function FilterChip({
  label,
  selected,
  onPress,
  leading,
  trailing,
  disabled = false,
  testID,
  accessibilityLabel,
}: FilterChipProps) {
  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ selected, disabled }}
      style={[
        styles.chip,
        selected && styles.chipSelected,
        disabled && styles.chipDisabled,
      ]}
    >
      {leading}
      <Text
        numberOfLines={1}
        style={[
          styles.chipText,
          styles.chipLabel,
          selected && styles.chipTextSelected,
        ]}
      >
        {label}
      </Text>
      {trailing}
    </Pressable>
  );
}

/**
 * The uppercase, letter-spaced heading above each pill row.
 */
export function FilterSectionTitle({
  children,
  style,
}: {
  children: ReactNode;
  style?: StyleProp<TextStyle>;
}) {
  return <Text style={[styles.sectionTitle, style]}>{children}</Text>;
}
